import { ApiService, ApiUserOauthClient } from "@incendium/api";
import { CheckCircle, ChevronRight } from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Stack,
  Typography,
  styled,
} from "@mui/material";
import { ConnectButton } from "features/dataConnectors";
import SyncConnectAdAccount from "features/dataConnectors/components/SyncConnectAdAccount";
import OnboardingMetaPixelField from "features/onboarding/components/OnboardingMetaPixelField";
import WooCommerceOnboardingPlatformLogoBox from "features/onboarding/woocommerce/WooCommerceOnboardingPlatformLogoBox";
import { WooAdPlatformConfig } from "features/onboarding/woocommerce/woocommerceAdPlatformTypes";
import { syncService } from "Apis";
import { useExternalSyncs } from "Hooks/useExternalSyncs";
import { useNotification, useSelectedProject } from "Hooks";
import { useSyncClients } from "Hooks/useSyncClients";
import produce from "immer";
import { useConfirmationContext } from "Providers/ConfirmationProvider";
import { useCallback, useEffect, useMemo, useState } from "react";

export type WooAdPlatformRowConfig = WooAdPlatformConfig & {
  logo: string;
};

const StyledAccordion = styled(Accordion)(({ theme }) => {
  const radius = theme.spacing(1.5);
  return {
    boxShadow: "none",
    border: `1px solid ${theme.palette.grey[200]}`,
    borderRadius: radius,
    overflow: "hidden",
    backgroundColor: theme.palette.background.paper,
    "&:before": { display: "none" },
    // Each row is its own card; override MUI's :first-of-type 4px top radius
    "&:first-of-type": {
      borderTopLeftRadius: radius,
      borderTopRightRadius: radius,
    },
    "&:last-of-type": {
      borderBottomLeftRadius: radius,
      borderBottomRightRadius: radius,
    },
    "& .MuiAccordionSummary-root": {
      borderRadius: 0,
    },
  };
});

interface IWooCommerceOnboardingAdPlatformRowProps {
  platform: WooAdPlatformRowConfig;
}

function pickOAuthClient(
  clients: ApiUserOauthClient[] | undefined,
  type: WooAdPlatformConfig["type"],
): ApiUserOauthClient | undefined {
  if (!clients?.length) {
    return undefined;
  }
  const readyForType = clients.filter((c) => c.readyToTypes?.includes(type));
  return readyForType[0] ?? clients[0];
}

function WooCommerceOnboardingAdPlatformRow({
  platform,
}: IWooCommerceOnboardingAdPlatformRowProps) {
  const { selectedProject } = useSelectedProject();
  const {
    syncClients,
    setSyncClients,
    getAccounts,
    accounts,
    accountsLoading,
  } = useSyncClients();
  const { externalSyncs, setExternalSyncs } = useExternalSyncs();
  const { openConfirmation, closeConfirmation } = useConfirmationContext();
  const { showSuccessNotification, showErrorNotification } = useNotification();
  const [expanded, setExpanded] = useState(false);
  const [fetchError, setFetchError] = useState(false);

  const connection = useMemo(
    () => pickOAuthClient(syncClients[platform.service], platform.type),
    [syncClients, platform.service, platform.type],
  );

  const isReady = useMemo(
    () => connection?.readyToTypes?.includes(platform.type) ?? false,
    [connection, platform.type],
  );

  const syncs = useMemo(() => {
    return externalSyncs.filter((ex) => ex.type === platform.type);
  }, [externalSyncs, platform.type]);

  const isLinked = syncs.length > 0;
  const hasReadyOAuth = Boolean(connection?.id && isReady);
  const canExpand = isLinked || hasReadyOAuth;
  const showConnect = !isLinked && !hasReadyOAuth;
  const isMeta = platform.service === ApiService.FACEBOOK;

  const removeAccount = useCallback(
    (syncId: number) => {
      const sync = syncs.find((s) => s.id === syncId);
      openConfirmation({
        title: "Remove this ad account?",
        body: "This will remove the connection and all associated campaign and click data for this account from the project.",
        callback: async () => {
          try {
            await syncService.syncServiceDeleteSync({
              projectId: selectedProject!.id as number,
              syncId,
            });
            setExternalSyncs(
              produce(externalSyncs, (draft) => {
                const idx = draft.findIndex((d) => d.id === syncId);
                if (idx >= 0) {
                  draft.splice(idx, 1);
                }
              }),
            );
            showSuccessNotification(
              `${sync?.accountName || "Account"} removed.`,
            );
          } catch {
            showErrorNotification("Failed to remove account.");
          }
          closeConfirmation();
        },
      });
    },
    [
      syncs,
      openConfirmation,
      closeConfirmation,
      selectedProject,
      externalSyncs,
      setExternalSyncs,
      showSuccessNotification,
      showErrorNotification,
    ],
  );

  useEffect(() => {
    if (!expanded || !connection?.id || !isReady) {
      return;
    }
    const load = async () => {
      try {
        await getAccounts(connection.id as number);
        setFetchError(false);
      } catch {
        setFetchError(true);
      }
    };
    load();
  }, [expanded, connection?.id, isReady, getAccounts]);

  const textButtonSx = {
    minWidth: "auto",
    fontWeight: 600,
    textTransform: "none" as const,
  };

  const openDetails = () => setExpanded(true);

  const summaryAction = isLinked ? (
    <Stack direction="row" alignItems="center" spacing={1}>
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <CheckCircle color="success" sx={{ fontSize: 18 }} />
        <Typography variant="body2" color="success.main" fontWeight={600}>
          Connected
        </Typography>
      </Stack>
      <Button
        variant="text"
        color="primary"
        size="small"
        sx={textButtonSx}
        onClick={(e) => {
          e.stopPropagation();
          setExpanded((v) => !v);
        }}
      >
        {expanded ? "Hide" : "Manage"}
      </Button>
    </Stack>
  ) : showConnect ? (
    <Box onClick={(e) => e.stopPropagation()}>
      <ConnectButton
        service={platform.service}
        syncClients={syncClients}
        setSyncClients={setSyncClients}
        onConnected={openDetails}
        connectButtonLabel="Connect"
        connectButtonProps={{
          variant: "text",
          color: "primary",
          size: "small",
          sx: textButtonSx,
        }}
      />
    </Box>
  ) : hasReadyOAuth ? (
    <Button
      variant="text"
      color="primary"
      size="small"
      sx={textButtonSx}
      onClick={(e) => {
        e.stopPropagation();
        openDetails();
      }}
    >
      Select account
    </Button>
  ) : null;

  return (
    <StyledAccordion
      disableGutters
      expanded={canExpand && expanded}
      onChange={(_, isExpanded) => {
        if (canExpand) {
          setExpanded(isExpanded);
        }
      }}
    >
      <AccordionSummary
        expandIcon={
          canExpand ? (
            <ChevronRight
              sx={{
                color: "text.secondary",
                transition: "transform 0.2s",
                transform: expanded ? "rotate(90deg)" : "rotate(0deg)",
              }}
            />
          ) : (
            false
          )
        }
        sx={{
          px: 2,
          minHeight: 56,
          backgroundColor: "background.paper",
          "& .MuiAccordionSummary-content": { my: 1.5, mr: 1 },
          "&.Mui-focusVisible": { backgroundColor: "transparent !important" },
          "&.Mui-expanded": { backgroundColor: "background.paper" },
          "&:hover": { backgroundColor: "background.paper" },
        }}
      >
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          sx={{ width: "100%", pr: 1 }}
        >
          <WooCommerceOnboardingPlatformLogoBox>
            <Box
              component="img"
              src={platform.logo}
              alt=""
              sx={{
                width: 28,
                height: 28,
                objectFit: "contain",
              }}
            />
          </WooCommerceOnboardingPlatformLogoBox>
          <Typography sx={{ flex: 1, fontWeight: 500 }}>
            {platform.title}
          </Typography>
          {summaryAction}
        </Stack>
      </AccordionSummary>

      {canExpand && (
        <AccordionDetails
          sx={{
            px: 2,
            pt: 2,
            pb: 2,
            bgcolor: "background.paper",
            borderTop: 1,
            borderColor: "grey.200",
          }}
        >
          {isLinked && (
            <Stack spacing={1} sx={{ mb: 2 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={600}
              >
                Linked accounts
              </Typography>
              {syncs.map((sync) => (
                <Stack
                  key={sync.id}
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  gap={2}
                >
                  <Typography variant="body2" sx={{ minWidth: 0 }}>
                    {sync.accountName
                      ? `${sync.accountName}${sync.account ? ` — ${sync.account.split("/").pop()}` : ""}`
                      : sync.account?.split("/").pop() ||
                        sync.account ||
                        "Ad account"}
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    color="secondary"
                    sx={{ flexShrink: 0 }}
                    onClick={() => removeAccount(sync.id as number)}
                  >
                    Remove
                  </Button>
                </Stack>
              ))}
            </Stack>
          )}
          <Box sx={{ "& > div": { px: 0, py: 0 } }}>
            <SyncConnectAdAccount
              oauthClient={connection}
              syncs={syncs}
              provider={platform.provider}
              type={platform.type}
              service={platform.service}
              error={fetchError}
              accountsLoading={accountsLoading}
              accounts={accounts}
              externalSyncs={externalSyncs}
              setExternalSyncs={setExternalSyncs}
              project={selectedProject!}
              syncClients={syncClients}
              setSyncClients={setSyncClients}
              description={
                <Typography variant="body2" color="text.secondary">
                  {isLinked
                    ? "Remove an account above or link another below."
                    : "Choose which ad account belongs to this project."}
                </Typography>
              }
            />
          </Box>
          {isMeta && <OnboardingMetaPixelField />}
        </AccordionDetails>
      )}
    </StyledAccordion>
  );
}

export default WooCommerceOnboardingAdPlatformRow;
